import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    Container,
    FormControl,
    MenuItem,
    Select,
    Skeleton,
    Tab,
    Tabs,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { TabContext } from "@mui/lab";
import Page from "src/components/Page";
import { makeStyles } from "tss-react/mui";
import { websiteTitle } from "src/config";
import { useTranslation } from "react-i18next";
import ApprovalsTable from "./ApprovalsTable";
import useAuth from "src/hooks/useAuth";
import GetPendingRequests from "src/api/GetPendingRequests";
import usePendingRequests from "src/hooks/usePendingRequests";
import { useSnackbar } from "notistack";
import { orderBy } from "lodash";
import { ChevronDown as KeyboardArrowDownIcon } from "lucide-react";

const useStyles = makeStyles()(theme => ({
    root: {
        backgroundColor: theme.palette.background.dark,
        minHeight: "100%",
        paddingBottom: theme.spacing(0.5),
    },
    metalRoot: {
        backgroundColor: "var(--neutral-1)",
        paddingBottom: theme.spacing(8),
        [theme.breakpoints.up("md")]: {
            padding: `0 ${theme.spacing(7)} ${theme.spacing(0.5)}`,
        },
    },
    reportFilterButton: {
        backgroundColor: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRight: "none",
        color: "var(--accent-9-contrast) !important",
        "&:hover, &.Mui-focusVisible": {
            backgroundColor: "var(--color-surface-raised)",
        },
        boxShadow: "0 0 0 0 transparent",
        textTransform: "none",
        padding: "var(--spacing-md, 8px) var(--spacing-xl, 16px)",
        minHeight: "40px !important",
        height: "40px",
        maxHeight: "40px",
    },
    selectedTab: {
        "& .Mui-selected": {
            backgroundColor: "var(--Colors-Background-bg-active, #1F242F);",
        },
        alignItems: "center !important",
        marginRight: 8,
        "& .MuiTab-root": {
            color: "var(--accent-9-contrast)",
        },
    },
    groupButtonMobile: {
        height: "50px",
        padding: "0",
        borderRadius: "0",
        borderRadius: "8px",
        textTransform: "none",
        fontWeight: "600",
        fontSize: "1rem",
        lineHeight: "25px",
        color: "#CECFD2",
        borderColor: "#333339",
        backgroundColor: "#1F242F",
        "& .MuiOutlinedInput-input": {
            padding: "6px 16px",
        },
        "& .MuiSelect-icon": {
            top: "unset",
        },
    },
}));

const ApprovalsView = props => {
    const theme = useTheme();
    const mediumDevice = useMediaQuery(theme.breakpoints.down("md"));
    const { classes } = useStyles();
    const { t } = useTranslation();
    const pageTitle = websiteTitle + " - " + t("approvals");
    const { enqueueSnackbar } = useSnackbar();
    const { getAccessToken, loggedInUser } = useAuth();
    const { pendingRequests, handlePendingRequestsUpdate } = usePendingRequests();
    const [loading, setLoading] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState({});

    const handleTabsChange = (event, value) => {
        setSelectedGroup(pendingRequests.filter(group => group.groupName === value)[0]);
        sessionStorage.setItem("approvalTabGroup", value);
    };

    const renderTabLabel = groupRequest => {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                }}
            >
                {groupRequest.groupName}
                <div
                    className="m-chip m-alert"
                    data-color-status="warning"
                    style={{
                        cursor: "pointer",
                        fontSize: "var(--font-size-xs)",
                        paddingTop: 14,
                        paddingBottom: 14,
                        marginLeft: 6,
                        marginRight: 0,
                    }}
                >
                    {groupRequest.requests.length}
                </div>
            </div>
        );
    };

    useEffect(() => {
        setLoading(true);
        const initializeApprovals = async () => {
            const { tokenAvailable, accessToken } = await getAccessToken();
            if (!tokenAvailable) {
                console.log(accessToken);
            }
            const { success, data } = await GetPendingRequests(accessToken, loggedInUser.id);
            if (success) {
                handlePendingRequestsUpdate(data);
            } else {
                enqueueSnackbar(data, {
                    variant: "error",
                });
            }
            setLoading(false);
        };
        initializeApprovals();
        return () => {
            sessionStorage.removeItem("approvalTabGroup");
        };
    }, []);

    useEffect(() => {
        if (pendingRequests.length) {
            if (sessionStorage.getItem("approvalTabGroup")) {
                setSelectedGroup(
                    pendingRequests.filter(
                        group => group.groupName === sessionStorage.getItem("approvalTabGroup")
                    )[0]
                );
            } else {
                setSelectedGroup(
                    orderBy(pendingRequests, [g => g.groupName, "id"], ["asc", "asc"])[0]
                );
            }
        }
    }, [pendingRequests]);

    return (
        <Page className={classes.metalRoot} title={pageTitle}>
            <Container maxWidth="xxl" style={mediumDevice ? { padding: "0 16px" } : { padding: 0 }}>
                {mediumDevice ? (
                    pendingRequests.length > 1 &&
                    selectedGroup?.groupName && (
                        <Box
                            display="flex"
                            flexDirection={"row"}
                            alignItems="center"
                            flexWrap={"wrap"}
                            p={"20px 10px"}
                            mt={"0px"}
                            style={{
                                backgroundColor: "var(--uishell-color-surface)",
                            }}
                        >
                            <FormControl
                                style={{
                                    width: "100%",
                                    padding: "0",
                                }}
                            >
                                <Select
                                    fullWidth
                                    className={classes.groupButtonMobile}
                                    renderValue={selectedValue => {
                                        return (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {selectedValue}
                                                <div
                                                    className="m-chip m-alert"
                                                    data-color-status="warning"
                                                    style={{
                                                        cursor: "pointer",
                                                        fontSize: "var(--font-size-xs)",
                                                        paddingTop: 14,
                                                        paddingBottom: 14,
                                                        marginLeft: 6,
                                                        marginRight: 0,
                                                    }}
                                                >
                                                    {selectedGroup.requests.length}
                                                </div>
                                            </div>
                                        );
                                    }}
                                    value={selectedGroup.groupName}
                                    IconComponent={KeyboardArrowDownIcon}
                                    onChange={e => handleTabsChange(e, e.target.value)}
                                >
                                    {orderBy(
                                        pendingRequests,
                                        [g => g.groupName, "id"],
                                        ["asc", "asc"]
                                    ).map((groupRequests, index) => {
                                        return (
                                            <MenuItem
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                                key={groupRequests.groupId}
                                                value={groupRequests.groupName}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        width: "100%",
                                                    }}
                                                >
                                                    {groupRequests.groupName}
                                                    <div
                                                        className="m-chip m-alert"
                                                        data-color-status="warning"
                                                        style={{
                                                            cursor: "pointer",
                                                            fontSize: "var(--font-size-xs)",
                                                            paddingTop: 14,
                                                            paddingBottom: 14,
                                                            marginLeft: 6,
                                                            marginRight: 0,
                                                        }}
                                                    >
                                                        {groupRequests.requests.length}
                                                    </div>
                                                </div>
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>
                    )
                ) : (
                    <TabContext value={selectedGroup.groupName}>
                        <Card
                            style={{
                                backgroundColor: "var(--uishell-color-surface)",
                            }}
                        >
                            <Box
                                display="flex"
                                flexDirection={"row"}
                                alignItems="center"
                                flexWrap={"wrap"}
                                p={"16px"}
                                mt={"0px"}
                            >
                                <Tabs
                                    value={selectedGroup.groupName}
                                    onChange={handleTabsChange}
                                    variant="scrollable"
                                    className={classes.selectedTab}
                                    scrollButtons={false}
                                    indicatorColor="transparent"
                                >
                                    {pendingRequests.length > 0 ? (
                                        orderBy(
                                            pendingRequests,
                                            [g => g.groupName, "id"],
                                            ["asc", "asc"]
                                        ).map((groupRequests, index) => {
                                            return (
                                                <Tab
                                                    key={groupRequests.groupId}
                                                    value={groupRequests.groupName}
                                                    label={renderTabLabel(groupRequests)}
                                                    className={classes.reportFilterButton}
                                                    style={
                                                        index === 0
                                                            ? {
                                                                  borderRadius: "8px 0 0 8px",
                                                              }
                                                            : index === pendingRequests.length - 1
                                                            ? {
                                                                  borderRadius: "0 8px 8px 0",
                                                                  borderRight:
                                                                      "1px solid var(--color-border)",
                                                              }
                                                            : {}
                                                    }
                                                />
                                            );
                                        })
                                    ) : (
                                        <Button
                                            disabled
                                            className={classes.reportFilterButton}
                                            style={{
                                                borderRadius: "8px",
                                                border: "1px solid var(--color-border)",
                                            }}
                                        >
                                            <Skeleton
                                                variant="rounded"
                                                width={50}
                                                height={20}
                                                sx={{
                                                    margin: "0 3px",
                                                    bgcolor: "var(--neutral-7)",
                                                }}
                                            />
                                        </Button>
                                    )}
                                </Tabs>
                            </Box>
                        </Card>
                    </TabContext>
                )}
                <Box style={{ marginTop: "16px" }}>
                    {!!selectedGroup && (
                        <ApprovalsTable
                            key={selectedGroup.groupId}
                            requestsList={selectedGroup.requests}
                            groupApprovalThreshold={selectedGroup.groupApprovalThreshold}
                            groupName={selectedGroup.groupName}
                            groupId={selectedGroup.groupId}
                            approvalsLoading={loading}
                            {...props}
                        />
                    )}
                </Box>
            </Container>
        </Page>
    );
};

export default ApprovalsView;
